import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import EditIcon from '@mui/icons-material/Edit';
import ErrorIcon from '@mui/icons-material/Error';
import FolderIcon from '@mui/icons-material/Folder';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import {
    Box,
    Button,
    Checkbox,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Divider,
    FormControl,
    FormControlLabel,
    FormLabel,
    Grid,
    IconButton,
    MenuItem,
    Paper,
    Radio,
    RadioGroup,
    TextField,
    Tooltip,
    Typography,
} from '@mui/material';
import axios from 'axios';
import { useEffect, useState } from 'react';
import { useVideoProcessing } from '../../contexts/VideoProcessingContext';
import splitVideoServiceInstance from '../../services/SplitVideoService';
import { validateItemName } from '../../utils/formatUtils';
import { splitVideoDialogHelpItems } from '../../utils/helpItems';
import HelpOverlay from '../common/HelpOverlay';
import PathSelectorDialog from '../dialogs/PathSelectorDialog';
import { SelectLanguageComponent, flatLangOptions } from './SelectLanguageComponent';
import { useTrackUsage } from '../../hooks/useTrackUsage';

const SUBTITLE_MODE_AI = 'ai';
const SUBTITLE_MODE_SKIP = 'skip';
const SUBTITLE_MODE_SRT = 'srt';

const SplitVideoDialog = ({
    open,
    onClose,
    folderPath,
    getLanguagesForCreateVideo,
    darkMode,
    themeVariant,
    onSuccess,
}) => {
    useTrackUsage('SplitVideoDialog', 'dialog', 'subtitles', open);
    const [videoName, setVideoName] = useState('');
    const [videoNameError, setVideoNameError] = useState(null);
    const [numberOfParts, setNumberOfParts] = useState(2);
    const [numberOfPartsError, setNumberOfPartsError] = useState(null);

    // Source type: 'local' or 'gdrive'
    const [selectedFile, setSelectedFile] = useState(null);
    const [googleLinkURL, setGoogleLinkURL] = useState('');
    const [checkingDriveLink, setCheckingDriveLink] = useState(false);
    const [driveLinkVerified, setDriveLinkVerified] = useState(false);
    const [driveLinkErrorMessage, setDriveLinkErrorMessage] = useState('');

    // Subtitle mode
    const [subtitleMode, setSubtitleMode] = useState(SUBTITLE_MODE_AI);
    const [srtFile, setSrtFile] = useState(null);
    const [srtFileError, setSrtFileError] = useState(null);
    const [srtLanguage, setSrtLanguage] = useState('English');
    const [englishSrtFile, setEnglishSrtFile] = useState(null);
    const [englishSrtFileError, setEnglishSrtFileError] = useState(null);

    // Options
    const [isCompressed, setIsCompressed] = useState(false);
    const [isLogging, setIsLogging] = useState(false);
    const [bitRate, setBitRate] = useState('115');
    const [crfValue, setCRFValue] = useState('28');

    // Path selector
    const [selectedPath, setSelectedPath] = useState(folderPath || '/');
    const [pathSelectorOpen, setPathSelectorOpen] = useState(false);

    // Uploading state
    const [isUploading, setIsUploading] = useState(false);
    const [statusMessage, setStatusMessage] = useState('');
    const [submitError, setSubmitError] = useState(null);
    const [helpOpen, setHelpOpen] = useState(false);

    const { trackVideo } = useVideoProcessing();

    // Keep selectedPath in sync with folderPath prop when dialog opens
    useEffect(() => {
        if (open && folderPath) {
            setSelectedPath(folderPath);
        }
    }, [open, folderPath]);

    const resetForm = () => {
        setVideoName('');
        setVideoNameError(null);
        setNumberOfParts(2);
        setNumberOfPartsError(null);
        setSelectedFile(null);
        setGoogleLinkURL('');
        setDriveLinkVerified(false);
        setDriveLinkErrorMessage('');
        setSubtitleMode(SUBTITLE_MODE_AI);
        setSrtFile(null);
        setSrtFileError(null);
        setSrtLanguage('English');
        setEnglishSrtFile(null);
        setEnglishSrtFileError(null);
        setIsCompressed(false);
        setIsLogging(false);
        setBitRate('115');
        setCRFValue('28');
        setSelectedPath(folderPath || '/');
        setIsUploading(false);
        setStatusMessage('');
        setSubmitError(null);
    };

    const handleClose = () => {
        resetForm();
        onClose();
    };

    const handleFileChange = (e) => {
        const file = e.target.files[0];
        if (file) {
            setSelectedFile(file);
            const nameWithoutExtension = file.name.replace(/\.[^/.]+$/, '');
            setVideoName(nameWithoutExtension);
            setVideoNameError(null);
            // Clear GDrive when local file is selected
            setGoogleLinkURL('');
            setDriveLinkVerified(false);
            setDriveLinkErrorMessage('');
        }
    };

    const handleGDriveLinkChange = (e) => {
        setGoogleLinkURL(e.target.value);
        setDriveLinkVerified(false);
        setDriveLinkErrorMessage('');
        // Clear local file when GDrive is entered
        setSelectedFile(null);
    };

    const verifyDriveLink = async () => {
        if (!googleLinkURL.trim()) {
            setDriveLinkErrorMessage('Please enter a Google Drive link');
            return false;
        }
        setCheckingDriveLink(true);
        try {
            const response = await axios.head(googleLinkURL, { timeout: 5000 });
            if (response.status === 200) {
                setDriveLinkVerified(true);
                setDriveLinkErrorMessage('');
                return true;
            } else {
                throw new Error('Non-200 status');
            }
        } catch {
            setDriveLinkVerified(false);
            setDriveLinkErrorMessage("This link appears to be restricted. Please make sure it's publicly accessible.");
            return false;
        } finally {
            setCheckingDriveLink(false);
        }
    };

    const handleSrtFileChange = (e) => {
        const file = e.target.files[0];
        if (!file) { setSrtFile(null); return; }
        const ext = file.name.split('.').pop().toLowerCase();
        if (ext !== 'srt') {
            alert('Please upload an SRT file.');
            e.target.value = '';
            setSrtFile(null);
            return;
        }
        if (file.size > 10 * 1024 * 1024) {
            alert('File too large. Please select a file smaller than 10MB.');
            e.target.value = '';
            setSrtFile(null);
            return;
        }
        setSrtFile(file);
    };

    const handleEnglishSrtFileChange = (e) => {
        const file = e.target.files[0];
        if (!file) { setEnglishSrtFile(null); return; }
        const ext = file.name.split('.').pop().toLowerCase();
        if (ext !== 'srt') {
            alert('Please upload an SRT file.');
            e.target.value = '';
            setEnglishSrtFile(null);
            return;
        }
        if (file.size > 10 * 1024 * 1024) {
            alert('File too large. Please select a file smaller than 10MB.');
            e.target.value = '';
            setEnglishSrtFile(null);
            return;
        }
        setEnglishSrtFile(file);
    };

    const validateNumberOfParts = (val) => {
        const n = parseInt(val, 10);
        if (isNaN(n) || n < 2) return 'Number of parts must be at least 2';
        if (n > 50) return 'Number of parts cannot exceed 50';
        return null;
    };

    const isSourceProvided = selectedFile !== null || (googleLinkURL.trim() !== '' && driveLinkVerified);
    const linkType = selectedFile ? 'local' : 'gdrive';
    const needsEnglishSrt = subtitleMode === SUBTITLE_MODE_SRT && srtLanguage !== 'English' && srtLanguage !== 'Gujarati';

    const canSubmit = () => {
        if (!isSourceProvided) return false;
        if (!videoName || !!videoNameError) return false;
        if (parseInt(numberOfParts, 10) < 2 || !!numberOfPartsError) return false;
        if (subtitleMode === SUBTITLE_MODE_SRT && !srtFile) return false;
        if (needsEnglishSrt && !englishSrtFile) return false;
        return true;
    };

    const handleSubmit = async () => {
        const nameError = validateItemName(videoName, 'video');
        if (nameError) { setVideoNameError(nameError); return; }

        const partsError = validateNumberOfParts(numberOfParts);
        if (partsError) { setNumberOfPartsError(partsError); return; }

        if (subtitleMode === SUBTITLE_MODE_SRT && !srtFile) {
            setSrtFileError('Please upload an SRT file before submitting.');
            return;
        }

        if (needsEnglishSrt && !englishSrtFile) {
            setEnglishSrtFileError('Please upload an English SRT file for this language.');
            return;
        }

        if (linkType === 'gdrive' && !driveLinkVerified) {
            const ok = await verifyDriveLink();
            if (!ok) return;
        }

        setIsUploading(true);
        setSubmitError(null);
        setStatusMessage('');
        try {
            const selectedLanguages = JSON.parse(localStorage.getItem('createVideoLanguages')) || [];

            let srtContent = null;
            if (subtitleMode === SUBTITLE_MODE_SRT && srtFile) {
                srtContent = await new Promise((resolve, reject) => {
                    const reader = new FileReader();
                    reader.onload = (e) => resolve(e.target.result);
                    reader.onerror = reject;
                    reader.readAsText(srtFile);
                });
            }

            let englishSrtContent = null;
            if (needsEnglishSrt && englishSrtFile) {
                englishSrtContent = await new Promise((resolve, reject) => {
                    const reader = new FileReader();
                    reader.onload = (e) => resolve(e.target.result);
                    reader.onerror = reject;
                    reader.readAsText(englishSrtFile);
                });
            }

            const parts = await splitVideoServiceInstance.splitAndProcess(
                {
                    videoName: videoName.trim(),
                    numberOfParts: parseInt(numberOfParts, 10),
                    linkType,
                    selectedFile,
                    googleLinkURL,
                    folderPath: selectedPath,
                    createLanguages: selectedLanguages.join(','),
                    isCompressed,
                    isLogging,
                    bitRate,
                    crfValue,
                    subtitleMode,
                    srtContent,
                    srtFileName: srtFile ? srtFile.name : null,
                    srtLanguage,
                    englishSrtContent,
                    englishSrtFileName: englishSrtFile ? englishSrtFile.name : null,
                },
                (msg) => setStatusMessage(msg),
            );

            // Track each created part in the progress panel
            if (Array.isArray(parts)) {
                const skipTranslation = subtitleMode !== SUBTITLE_MODE_AI;
                parts.forEach((part) => {
                    if (part?.id) {
                        trackVideo(part.id, part.videoName, skipTranslation, 'video');
                    }
                });
            }

            handleClose();
            if (onSuccess) onSuccess();
        } catch (err) {
            console.error('Error in Split & Process:', err);
            setSubmitError(err?.message || 'Split & Process failed. Please try again.');
            setIsUploading(false);
        }
    };

    const formatPath = (path) => {
        if (!path || path === '/') return 'Home';
        return path;
    };

    return (
        <>
            <Dialog
                open={open}
                onClose={handleClose}
                fullWidth
                maxWidth="md"
                PaperProps={{ sx: { borderRadius: 2 } }}
                data-testid="split-video-dialog"
            >
                <DialogTitle>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                        <Typography variant="h6" component="div" data-testid="split-video-dialog-title">
                            Split Video into Parts
                        </Typography>
                        <Tooltip title="Help Guide">
                            <IconButton size="small" onClick={() => setHelpOpen(true)}>
                                <HelpOutlineIcon />
                            </IconButton>
                        </Tooltip>
                    </Box>
                    <Typography variant="body2" color="text.secondary">
                        Upload a video, choose how many equal parts to split it into, and each part will be
                        processed automatically.
                    </Typography>
                </DialogTitle>

                <DialogContent>
                    {/* Video Name */}
                    <TextField
                        label="Video Name (base)"
                        variant="outlined"
                        fullWidth
                        margin="normal"
                        value={videoName}
                        onChange={(e) => {
                            setVideoName(e.target.value);
                            setVideoNameError(validateItemName(e.target.value, 'video'));
                        }}
                        onBlur={() => setVideoNameError(validateItemName(videoName, 'video'))}
                        required
                        error={!!videoNameError}
                        helperText={videoNameError || 'Parts will be named: Video Name - Part 1, Part 2, …'}
                        data-testid="split-video-name-input"
                    />

                    {/* Number of Parts */}
                    <Box sx={{ position: 'relative' }}>
                    <TextField
                        label="Number of Parts"
                        type="number"
                        variant="outlined"
                        fullWidth
                        margin="normal"
                        value={numberOfParts}
                        onChange={(e) => {
                            setNumberOfParts(e.target.value);
                            setNumberOfPartsError(validateNumberOfParts(e.target.value));
                        }}
                        inputProps={{ min: 2, max: 50 }}
                        required
                        error={!!numberOfPartsError}
                        helperText={numberOfPartsError || 'Video will be split into equal-duration parts'}
                        data-testid="split-video-parts-input"
                        id="split-number-of-parts"
                        InputProps={{}}
                    />
                    </Box>

                    {/* Destination Folder */}
                    <Box sx={{ mt: 2, mb: 2 }}>
                        <Typography variant="body2" color="text.secondary" gutterBottom>
                            Destination Folder:
                        </Typography>
                        <Paper
                            variant="outlined"
                            sx={{
                                p: 1.5,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'space-between',
                                borderRadius: 1,
                            }}
                        >
                            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                <FolderIcon color="primary" sx={{ mr: 1 }} />
                                <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                    {formatPath(selectedPath)}
                                </Typography>
                            </Box>
                            <Tooltip title="Change destination folder">
                                <IconButton
                                    size="small"
                                    color="primary"
                                    onClick={() => setPathSelectorOpen(true)}
                                    aria-label="Change folder"
                                >
                                    <EditIcon fontSize="small" />
                                </IconButton>
                            </Tooltip>
                        </Paper>
                    </Box>

                    {/* Video Source */}
                    <Paper id="split-video-source" variant="outlined" sx={{ p: 2, mt: 2, borderRadius: 1 }}>
                        <Box sx={{ mb: 1 }}>
                            <Typography variant="subtitle2">Video Source</Typography>
                        </Box>
                        <Grid container spacing={2} direction="column">
                            {/* Google Drive */}
                            <Grid item>
                                <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                                    <TextField
                                        label="Google Drive Link"
                                        variant="outlined"
                                        fullWidth
                                        value={googleLinkURL}
                                        onChange={handleGDriveLinkChange}
                                        onBlur={() => {
                                            if (googleLinkURL.trim() && !driveLinkVerified && !checkingDriveLink) {
                                                setTimeout(() => verifyDriveLink(), 300);
                                            }
                                        }}
                                        InputProps={{
                                            endAdornment: driveLinkVerified ? (
                                                <CheckCircleIcon color="success" sx={{ mr: 1 }} />
                                            ) : null,
                                        }}
                                        data-testid="split-google-drive-input"
                                    />
                                    <Button
                                        variant="outlined"
                                        color="primary"
                                        disabled={!googleLinkURL || checkingDriveLink}
                                        onClick={verifyDriveLink}
                                        sx={{ minWidth: '120px', height: '56px' }}
                                        data-testid="split-verify-drive-btn"
                                    >
                                        {checkingDriveLink ? <CircularProgress size={24} /> : 'Check Access'}
                                    </Button>
                                </Box>
                                {googleLinkURL && driveLinkErrorMessage && (
                                    <Typography variant="caption" color="error" sx={{ display: 'block', mt: 0.5 }}>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                            <ErrorIcon fontSize="small" />
                                            {driveLinkErrorMessage}
                                        </Box>
                                    </Typography>
                                )}
                                {driveLinkVerified && (
                                    <Typography variant="caption" color="success.main" sx={{ display: 'block', mt: 0.5 }}>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                            <CheckCircleIcon fontSize="small" />
                                            Link is accessible and ready for processing
                                        </Box>
                                    </Typography>
                                )}
                            </Grid>

                            <Grid item>
                                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                    <Divider sx={{ flexGrow: 1 }} />
                                    <Typography variant="body2" color="text.secondary" sx={{ px: 1 }}>
                                        or
                                    </Typography>
                                    <Divider sx={{ flexGrow: 1 }} />
                                </Box>
                            </Grid>

                            {/* Local File */}
                            <Grid item>
                                <Button
                                    variant="outlined"
                                    component="label"
                                    fullWidth
                                    sx={{ py: 1.5, textTransform: 'none' }}
                                    data-testid="split-local-file-btn"
                                >
                                    {selectedFile ? 'Change Selected File' : 'Upload Local File'}
                                    <input
                                        type="file"
                                        accept="video/*, audio/*"
                                        hidden
                                        onChange={handleFileChange}
                                        data-testid="split-local-file-input"
                                    />
                                </Button>
                                {selectedFile && (
                                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                                        Selected: {selectedFile.name}
                                    </Typography>
                                )}
                            </Grid>
                        </Grid>
                    </Paper>

                    {/* Language Selection */}
                    <Typography variant="h6" sx={{ mt: 3, mb: 1, fontWeight: 600 }}>
                        Language Selection
                    </Typography>
                    <Box sx={{ my: 2 }}>
                        <SelectLanguageComponent
                            id="splitVideoLanguageDropdown"
                            disabled={false}
                            languageOptions={getLanguagesForCreateVideo}
                            darkMode={darkMode}
                            themeVariant={themeVariant}
                            isDarkMode={darkMode === true}
                            showGrouped={true}
                        />
                    </Box>

                    {/* Subtitle Mode */}
                    <Paper id="split-subtitle-mode" variant="outlined" sx={{ p: 2, mt: 2, borderRadius: 1 }}>
                        <FormControl>
                            <FormLabel>
                                <Typography variant="subtitle2">Subtitle Mode</Typography>
                            </FormLabel>
                            <RadioGroup
                                value={subtitleMode}
                                onChange={(e) => setSubtitleMode(e.target.value)}
                            >
                                <FormControlLabel
                                    value={SUBTITLE_MODE_AI}
                                    control={<Radio />}
                                    label={
                                        <Box>
                                            <Typography variant="body2">AI-generated subtitles</Typography>
                                            <Typography variant="caption" color="text.secondary">
                                                Each part will be sent to the speech-to-text AI server
                                            </Typography>
                                        </Box>
                                    }
                                    data-testid="subtitle-mode-ai"
                                />
                                <FormControlLabel
                                    value={SUBTITLE_MODE_SKIP}
                                    control={<Radio />}
                                    label={
                                        <Box>
                                            <Typography variant="body2">Skip subtitles (empty files)</Typography>
                                            <Typography variant="caption" color="text.secondary">
                                                Generate empty subtitle files for all selected languages
                                            </Typography>
                                        </Box>
                                    }
                                    data-testid="subtitle-mode-skip"
                                />
                                <FormControlLabel
                                    value={SUBTITLE_MODE_SRT}
                                    control={<Radio />}
                                    label={
                                        <Box>
                                            <Typography variant="body2">Upload SRT</Typography>
                                            <Typography variant="caption" color="text.secondary">
                                                Upload an SRT file — it will be split by timestamp and assigned to each part
                                            </Typography>
                                        </Box>
                                    }
                                    data-testid="subtitle-mode-srt"
                                />
                            </RadioGroup>
                        </FormControl>

                        {subtitleMode === SUBTITLE_MODE_SRT && (
                            <Box sx={{ mt: 2 }}>
                                <Button
                                    variant="outlined"
                                    component="label"
                                    fullWidth
                                    sx={{ py: 1.5, textTransform: 'none' }}
                                    data-testid="split-srt-upload-btn"
                                >
                                    {srtFile ? `Selected: ${srtFile.name}` : 'Upload SRT File'}
                                    <input
                                        type="file"
                                        accept=".srt"
                                        hidden
                                        onChange={(e) => { handleSrtFileChange(e); setSrtFileError(null); }}
                                        data-testid="split-srt-file-input"
                                    />
                                </Button>
                                {srtFileError && (
                                    <Typography variant="caption" color="error" sx={{ display: 'block', mt: 0.5 }}>
                                        {srtFileError}
                                    </Typography>
                                )}
                                {srtFile && (
                                    <Typography variant="caption" color="success.main" sx={{ display: 'block', mt: 0.5 }}>
                                        <CheckCircleIcon fontSize="small" sx={{ mr: 0.5, verticalAlign: 'middle' }} />
                                        {srtFile.name} | {(srtFile.size / 1024).toFixed(1)} KB
                                    </Typography>
                                )}
                                <TextField
                                    select
                                    label="SRT Language"
                                    value={srtLanguage}
                                    onChange={(e) => setSrtLanguage(e.target.value)}
                                    fullWidth
                                    size="small"
                                    sx={{ mt: 1.5 }}
                                    data-testid="split-srt-language-select"
                                >
                                    {flatLangOptions.map((lang) => (
                                        <MenuItem key={lang.value} value={lang.label}>
                                            {lang.label}
                                        </MenuItem>
                                    ))}
                                </TextField>
                                {(() => {
                                    const selectedLangs = JSON.parse(localStorage.getItem('createVideoLanguages')) || [];
                                    if (selectedLangs.length > 0 && !selectedLangs.includes(srtLanguage)) {
                                        return (
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 1 }}>
                                                <ErrorIcon fontSize="small" color="warning" />
                                                <Typography variant="caption" color="warning.main">
                                                    "{srtLanguage}" is not in your Language Selection above. Add it so the SRT content is assigned to that language.
                                                </Typography>
                                            </Box>
                                        );
                                    }
                                    return null;
                                })()}
                                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                                    The SRT will be split by timestamp — cues for each time segment will be
                                    assigned to the corresponding video part with re-zeroed timestamps.
                                </Typography>

                                {needsEnglishSrt && (
                                    <Box sx={{ mt: 2, p: 1.5, border: '1px solid', borderColor: 'warning.main', borderRadius: 1 }}>
                                        <Typography variant="body2" sx={{ fontWeight: 500, mb: 1 }}>
                                            English SRT (required for non-English / non-Gujarati languages)
                                        </Typography>
                                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                                            Upload the English version of this SRT — it will also be split by timestamp and stored alongside the {srtLanguage} subtitles.
                                        </Typography>
                                        <Button
                                            variant="outlined"
                                            component="label"
                                            fullWidth
                                            sx={{ py: 1.5, textTransform: 'none' }}
                                            data-testid="split-english-srt-upload-btn"
                                        >
                                            {englishSrtFile ? `Selected: ${englishSrtFile.name}` : 'Upload English SRT File'}
                                            <input
                                                type="file"
                                                accept=".srt"
                                                hidden
                                                onChange={(e) => { handleEnglishSrtFileChange(e); setEnglishSrtFileError(null); }}
                                                data-testid="split-english-srt-file-input"
                                            />
                                        </Button>
                                        {englishSrtFileError && (
                                            <Typography variant="caption" color="error" sx={{ display: 'block', mt: 0.5 }}>
                                                {englishSrtFileError}
                                            </Typography>
                                        )}
                                        {englishSrtFile && (
                                            <Typography variant="caption" color="success.main" sx={{ display: 'block', mt: 0.5 }}>
                                                <CheckCircleIcon fontSize="small" sx={{ mr: 0.5, verticalAlign: 'middle' }} />
                                                {englishSrtFile.name} | {(englishSrtFile.size / 1024).toFixed(1)} KB
                                            </Typography>
                                        )}
                                    </Box>
                                )}
                            </Box>
                        )}
                    </Paper>

                    {/* Advanced Options */}
                    <Typography variant="h6" sx={{ mt: 3, mb: 1, fontWeight: 150 }}>
                        Advanced Options
                    </Typography>
                    <Grid container spacing={2}>
                        <Grid item xs={12}>
                            <Box sx={{ display: 'flex', flexDirection: 'row', gap: 4, flexWrap: 'wrap' }}>
                                <FormControlLabel
                                    control={
                                        <Checkbox
                                            checked={isCompressed}
                                            onChange={(e) => setIsCompressed(e.target.checked)}
                                            data-testid="split-compressed-checkbox"
                                        />
                                    }
                                    label={
                                        <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                                            <Typography variant="body2">Compressed</Typography>
                                            <Typography variant="caption" color="text.secondary">
                                                Apply H.264 compression to each part
                                            </Typography>
                                        </Box>
                                    }
                                />
                                <FormControlLabel
                                    control={
                                        <Checkbox
                                            checked={isLogging}
                                            onChange={(e) => setIsLogging(e.target.checked)}
                                            data-testid="split-logging-checkbox"
                                        />
                                    }
                                    label={
                                        <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                                            <Typography variant="body2">Detailed Logging</Typography>
                                            <Typography variant="caption" color="text.secondary">
                                                Enable verbose server logs
                                            </Typography>
                                        </Box>
                                    }
                                />
                            </Box>
                        </Grid>
                    </Grid>
                </DialogContent>

                <DialogActions sx={{ p: 2, pt: 1.5, flexDirection: 'column', alignItems: 'stretch', gap: 1 }}>
                    {submitError && (
                        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, px: 1, py: 0.5, bgcolor: 'error.light', borderRadius: 1 }}>
                            <ErrorIcon fontSize="small" color="error" sx={{ mt: 0.3, flexShrink: 0 }} />
                            <Typography variant="body2" color="error.contrastText" sx={{ wordBreak: 'break-word' }}>
                                {submitError}
                            </Typography>
                        </Box>
                    )}
                    {isUploading && statusMessage && (
                        <Typography variant="caption" color="text.secondary" sx={{ px: 1, display: 'block' }}>
                            {statusMessage}
                        </Typography>
                    )}
                    <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
                        <Button onClick={handleClose} color="inherit" disabled={isUploading} data-testid="split-cancel-btn">
                            Cancel
                        </Button>
                        <Button
                            variant="contained"
                            color="primary"
                            disabled={!canSubmit() || isUploading}
                            onClick={handleSubmit}
                            data-testid="split-submit-btn"
                        >
                            {isUploading ? <CircularProgress size={22} sx={{ mr: 1 }} /> : null}
                            {isUploading ? 'Processing…' : 'Split & Process'}
                        </Button>
                    </Box>
                </DialogActions>
            </Dialog>

            <PathSelectorDialog
                open={pathSelectorOpen}
                onClose={() => setPathSelectorOpen(false)}
                onSelect={(path) => { setSelectedPath(path); setPathSelectorOpen(false); }}
                currentPath={selectedPath}
                title="Select Destination Folder"
            />
            <HelpOverlay
                helpItems={splitVideoDialogHelpItems}
                title="Split Video Guide"
                showButton={false}
                open={helpOpen}
                onClose={() => setHelpOpen(false)}
            />
        </>
    );
};

export default SplitVideoDialog;
